import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import Heatmap from "../components/Heatmap";
import Rubric from "../components/Rubric";
import Reflections from "../components/Reflections";
import Respondents from "../components/Respondents";
import FilterBar from "../components/FilterBar";
import ConfirmModal from "../components/ConfirmModal";
import DrillDownPanel from "../components/DrillDownPanel";
import Divergence from "../components/Divergence";
import Compare from "../components/Compare";
import Benchmark from "../components/Benchmark";
import Cohorts from "../components/Cohorts";
import { MarkedItemsProvider } from "../lib/markedItems";
import MarkedItemsTray from "../components/MarkedItemsTray";
import {
  MarkedHeaderBadge,
  RawExportButton,
  IndividualReportsToggle,
  FilteredCohortCard,
} from "../components/deployment_detail/DeploymentHeaderBits";
import EditDeploymentModal from "../components/EditDeploymentModal";
import SendLinkButton from "../components/SendLinkButton";
import IndividualClientView from "../components/IndividualClientView";
import ResourceSummary from "../components/ResourceSummary";

const TABS = [
  { key: "respondents", label: "Respondents" },
  { key: "heatmap", label: "Heatmap" },
  { key: "rubric", label: "Rubric" },
  { key: "divergence", label: "Divergence" },
  { key: "compare", label: "Compare" },
  { key: "benchmark", label: "Benchmark" },
  { key: "cohorts", label: "Cohorts" },
  { key: "reflections", label: "Reflections" },
];

// Sprint 11 Phase 2 — Individual deployments have a single client, so
// every cohort-shaped surface (heatmap, rubric distribution,
// divergence, compare across segments, benchmark vs corpus, cohort
// definitions) is meaningless and is hidden. What remains is the
// IndividualClientView (default) plus Reflections (qualitative
// responses) for the coach's debrief prep.
const INDIVIDUAL_TABS = [
  { key: "profile", label: "Profile" },
  { key: "reflections", label: "Reflections" },
];

export default function DeploymentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Default tab: respondents for draft (operational), heatmap once open/closed.
  // The first useEffect below corrects this once the deployment loads.
  const tab = searchParams.get("tab") || "heatmap";
  const metric = searchParams.get("metric") || "mean";
  const drillParam = searchParams.get("drill");  // <dim>:<segment_key>:<segment_value>  or  <dim>:_all
  const filters = useMemo(() => {
    const out = {};
    for (const [k, v] of searchParams.entries()) {
      if (!["tab", "metric", "drill", "compare_a", "compare_b", "cohort"].includes(k)) out[k] = v;
    }
    return out;
  }, [searchParams]);

  // Parse the drill URL parameter into structured form.
  // Format: "<dimension>:_all" or "<dimension>:<segment_key>:<segment_value>".
  // Returns null if not present or malformed.
  const drillTarget = useMemo(() => {
    if (!drillParam || tab !== "heatmap") return null;
    const parts = drillParam.split(":");
    if (parts.length < 2) return null;
    const [dim, ...rest] = parts;
    if (rest[0] === "_all") {
      return { dimensionKey: dim, segmentKey: "_all", segmentValue: "All respondents" };
    }
    if (rest.length === 2) {
      return { dimensionKey: dim, segmentKey: rest[0], segmentValue: rest[1] };
    }
    return null;
  }, [drillParam, tab]);

  function openDrill(seg, dim) {
    const next = new URLSearchParams(searchParams);
    const value = seg.segment_key === "_all"
      ? `${dim.dimension}:_all`
      : `${dim.dimension}:${seg.segment_key}:${seg.segment_value}`;
    next.set("drill", value);
    next.set("tab", "heatmap");
    setSearchParams(next, { replace: false });
  }
  function closeDrill() {
    const next = new URLSearchParams(searchParams);
    next.delete("drill");
    setSearchParams(next, { replace: false });
  }
  const drillTriggerSelector = drillTarget
    ? `[data-testid="cell-${CSS.escape(drillTarget.segmentValue)}-${CSS.escape(drillTarget.dimensionKey)}"]`
    : null;

  const [deployment, setDeployment] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [scores, setScores] = useState(null);
  const [rubric, setRubric] = useState(null);
  const [error, setError] = useState("");
  const [reportState, setReportState] = useState({ generating: false, message: "" });
  const [transition, setTransition] = useState(null);  // null | "open" | "close"
  const [statusBusy, setStatusBusy] = useState(false);
  const [orgTrendsEligible, setOrgTrendsEligible] = useState(false);
  const [editing, setEditing] = useState(false);

  async function reloadDeployment() {
    const d = await api.get(`/api/deployments/${id}`);
    setDeployment(d.data);
    return d.data;
  }

  useEffect(() => {
    (async () => {
      try {
        const [d, h, r] = await Promise.all([
          api.get(`/api/deployments/${id}`),
          api.get(`/api/deployments/${id}/heatmap`),
          api.get(`/api/deployments/${id}/rubric`),
        ]);
        setDeployment(d.data);
        setHeatmap(h.data);
        setRubric(r.data);
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [id]);

  // First-load tab default: draft → respondents; otherwise → heatmap.
  // Separate from the data-fetch effect so searchParams can be a proper
  // dependency without re-fetching every time the tab flips.
  useEffect(() => {
    if (!deployment) return;
    if (searchParams.get("tab")) return;
    // Sprint 11 — individual mode opens straight on the client profile;
    // no respondent tab, no heatmap. Organizational drafts open on
    // respondents (invite flow), everything else on heatmap.
    let defaultTab = "heatmap";
    if (deployment.deployment_mode === "individual") {
      defaultTab = "profile";
    } else if (deployment.status === "draft") {
      defaultTab = "respondents";
    }
    const next = new URLSearchParams(searchParams);
    next.set("tab", defaultTab);
    setSearchParams(next, { replace: true });
  }, [deployment, searchParams, setSearchParams]);

  // Fetch /scores with current filters whenever filters change.
  useEffect(() => {
    (async () => {
      try {
        const params = Object.keys(filters).length
          ? { filters: JSON.stringify(filters) }
          : undefined;
        const { data } = await api.get(`/api/deployments/${id}/scores`, { params });
        setScores(data);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      }
    })();
  }, [id, filters]);

  // After the deployment loads, check if its org has >=2 usable waves so we
  // can surface the "Compare across waves →" link.
  useEffect(() => {
    if (!deployment?.org_id) return;
    (async () => {
      try {
        const { data } = await api.get("/api/organizations/trends-summary");
        const row = (data.summaries || []).find((s) => s.org_id === deployment.org_id);
        setOrgTrendsEligible((row?.usable_wave_count || 0) >= 2);
      } catch (_) { /* non-fatal */ }
    })();
  }, [deployment?.org_id]);

  function setTab(key) {
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next);
  }
  function setMetric(key) {
    const next = new URLSearchParams(searchParams);
    next.set("metric", key);
    setSearchParams(next);
  }
  function setFilters(nextObj) {
    const next = new URLSearchParams();
    if (tab) next.set("tab", tab);
    if (metric) next.set("metric", metric);
    Object.entries(nextObj).forEach(([k, v]) => v && next.set(k, v));
    setSearchParams(next);
  }

  const availableFilters = deployment?.demographic_config || {};

  async function downloadReport() {
    if (!deployment) return;
    setReportState({ generating: true, message: "Preparing the report…" });
    try {
      const { data } = await api.post(`/api/deployments/${id}/report`);
      // Open the signed download URL in a new tab; the browser handles save-as
      // using the Content-Disposition header from /api/reports/download.
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`;
      window.location.href = fullUrl;
      setReportState({ generating: false, message: "" });
    } catch (e) {
      setReportState({
        generating: false,
        message: e?.response?.data?.detail || e.message || "Could not generate report.",
      });
    }
  }

  const canDownloadReport =
    deployment && deployment.completed >= 5 && (user?.role === "consultant" || user?.role === "super_admin");
  const canTransition =
    deployment && (user?.role === "consultant" || user?.role === "super_admin");

  async function applyStatus(next) {
    try {
      setStatusBusy(true);
      await api.post(`/api/deployments/${id}/status`, { status: next });
      await reloadDeployment();
      setTransition(null);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setStatusBusy(false);
    }
  }

  return (
    <MarkedItemsProvider deploymentId={id}>
      <div className="min-h-screen bg-paper" data-testid="deployment-detail">
      <header className="border-b border-gray-100 sticky top-0 z-10 bg-paper">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/dashboard")} className="btn-quiet text-xs" data-testid="back-to-dashboard">
              ← All deployments
            </button>
            <span className="wordmark text-sm">
              <span style={{ color: "#1A1A1A" }}>RESILIENT</span>{" "}
              <span style={{ color: "#2F6B7C" }}>TEAMS</span>
            </span>
          </div>
          <div className="flex items-center gap-6">
            <MarkedHeaderBadge />
            <RawExportButton
              deploymentId={id}
              completedCount={deployment?.completed || heatmap?.n || 0}
              deploymentMode={deployment?.deployment_mode}
            />
            {orgTrendsEligible && deployment?.org_id && (
              <button
                onClick={() => navigate(`/dashboard/orgs/${deployment.org_id}/trends`)}
                data-testid="compare-across-waves-link"
                style={{
                  fontFamily: "Oswald",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  fontSize: "11px",
                  color: "#2F6B7C",
                  border: "1px solid #4592A8",
                  background: "transparent",
                  padding: "8px 14px",
                  cursor: "pointer",
                }}
              >
                Compare across waves →
              </button>
            )}
            {canDownloadReport && (
              <>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/deployments/${id}/report/preview`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-quiet"
                  style={{
                    fontFamily: "Oswald",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    fontSize: "11px",
                    color: "#555",
                    border: "1px solid #555",
                    padding: "8px 14px",
                    textDecoration: "none",
                  }}
                  data-testid="preview-report-link"
                >
                  Preview report
                </a>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/deployments/${id}/report.pptx`}
                  className="btn-quiet"
                  style={{
                    fontFamily: "Oswald",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    fontSize: "11px",
                    color: "#2F6B7C",
                    border: "1px solid #2F6B7C",
                    padding: "8px 14px",
                    textDecoration: "none",
                  }}
                  data-testid="download-pptx-link"
                >
                  Download deck (.pptx)
                </a>
                <button
                  onClick={downloadReport}
                  disabled={reportState.generating}
                  className="btn-quiet"
                  style={{
                    fontFamily: "Oswald",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    fontSize: "11px",
                    color: reportState.generating ? "#999" : "#AD393E",
                    border: "1px solid currentColor",
                    padding: "8px 14px",
                    cursor: reportState.generating ? "wait" : "pointer",
                  }}
                  data-testid="download-report-button"
                >
                  {reportState.generating ? "Preparing…" : "Download report"}
                </button>
              </>
            )}
            <div className="text-sm text-gray-700 hidden md:block">{user?.name}</div>
            <button onClick={logout} className="btn-quiet" data-testid="logout-button">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <ConfirmModal
        open={transition === "open"}
        title="Open this deployment?"
        body="Once open, respondents can submit responses to their survey URLs. You can close it later, but you cannot return to draft."
        confirmLabel={statusBusy ? "Opening…" : "Open deployment"}
        onConfirm={() => applyStatus("open")}
        onClose={() => !statusBusy && setTransition(null)}
        testId="open-modal"
      />
      <ConfirmModal
        open={transition === "close"}
        title="Close this deployment?"
        body="Once closed, respondent URLs will stop accepting submissions. Analytics, the report, and the reflections tab remain available."
        confirmLabel={statusBusy ? "Closing…" : "Close deployment"}
        destructive
        onConfirm={() => applyStatus("closed")}
        onClose={() => !statusBusy && setTransition(null)}
        testId="close-modal"
      />

      <main id="main-content" className="max-w-7xl mx-auto px-8 py-12">
        {error && (
          <div className="panel mb-8" style={{ borderColor: "#AD393E" }} data-testid="error-banner">
            <div className="eyebrow mb-2" style={{ color: "#AD393E" }}>Error</div>
            <div className="text-sm">{error}</div>
          </div>
        )}

        {reportState.message && !reportState.generating && (
          <div className="panel mb-8" style={{ borderColor: "#AD393E" }} data-testid="report-error-banner">
            <div className="eyebrow mb-2" style={{ color: "#AD393E" }}>Report</div>
            <div className="text-sm">{reportState.message}</div>
          </div>
        )}

        {deployment && (
          <header className="mb-10 flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="eyebrow mb-2">
                {deployment.organization?.name} · Wave {deployment.wave_number} · {deployment.status}
              </div>
              <h1 style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }} className="mb-3">
                {deployment.name}
              </h1>
              <div className="text-sm text-gray-700">
                {deployment.completed} of {deployment.invited} respondents completed
              </div>
              <IndividualReportsToggle
                deploymentId={id}
                initial={deployment.individual_reports_enabled}
                canEdit={canTransition}
              />
            </div>
            {canTransition && (
              <div className="flex items-center gap-3 flex-wrap">
                {deployment.deployment_mode === "individual" && (
                  <SendLinkButton
                    deploymentId={deployment.id}
                    clientName={deployment.client_name || "client"}
                    lastSentAt={deployment.last_link_sent_at}
                    variant="header"
                    onSent={() => reloadDeployment()}
                  />
                )}
                {deployment.deployment_mode === "individual" && user?.role === "super_admin" && (
                  <Link
                    to={`/dashboard/admin/audit/suppression-bypass?deployment_id=${deployment.id}`}
                    data-testid="header-audit-log-link"
                    title="Review every access of this client's data"
                    style={{
                      fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                      fontSize: "11px", color: "#1A1A1A",
                      background: "transparent",
                      border: "1px solid #DDDDDD",
                      padding: "8px 14px",
                      textDecoration: "none",
                    }}
                  >
                    Audit log →
                  </Link>
                )}
                <button
                  onClick={() => setEditing(true)}
                  data-testid="edit-deployment-button"
                  className="btn-quiet"
                  style={{
                    fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                    fontSize: "11px", color: "#1A1A1A", background: "transparent",
                    border: "1px solid #DDDDDD", padding: "8px 14px",
                  }}
                >
                  Edit
                </button>
                {deployment.status === "draft" && (
                  <button
                    onClick={() => setTransition("open")}
                    data-testid="status-open-button"
                    style={{
                      fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                      fontSize: "11px", color: "#FBFAF7", background: "#4592A8",
                      border: 0, padding: "10px 18px", cursor: "pointer",
                    }}
                  >
                    Open deployment
                  </button>
                )}
                {deployment.status === "open" && (
                  <button
                    onClick={() => setTransition("close")}
                    data-testid="status-close-button"
                    style={{
                      fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                      fontSize: "11px", color: "#AD393E",
                      border: "1px solid #AD393E", background: "transparent",
                      padding: "10px 18px", cursor: "pointer",
                    }}
                  >
                    Close deployment
                  </button>
                )}
                {deployment.status === "closed" && (
                  <span
                    className="text-xs px-3 py-1"
                    style={{
                      fontFamily: "Oswald", letterSpacing: "0.15em", textTransform: "uppercase",
                      background: "#EEEEEE", color: "#555555",
                    }}
                    data-testid="status-closed-badge"
                  >
                    Closed
                  </span>
                )}
              </div>
            )}
          </header>
        )}

        {/* Tabs */}
        <div role="tablist" className="flex items-center gap-1 border-b border-gray-100 mb-10" data-testid="tabs">
          {(deployment?.deployment_mode === "individual" ? INDIVIDUAL_TABS : TABS)
            .map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.key)}
                className="px-5 py-3 text-sm transition-colors duration-100"
                style={{
                  fontFamily: "Oswald",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  color: active ? "#AD393E" : "#555555",
                  borderBottom: active ? "2px solid #AD393E" : "2px solid transparent",
                  marginBottom: "-1px",
                }}
                data-testid={`tab-${t.key}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "profile" && deployment && (
          <IndividualClientView deploymentId={id} />
        )}

        {tab === "respondents" && deployment && (
          <Respondents
            deploymentId={id}
            demographicConfig={deployment.demographic_config}
            status={deployment.status}
            deploymentMode={deployment.deployment_mode}
            onEditDemographics={() => setEditing(true)}
          />
        )}

        {tab === "heatmap" && (
          <div className="space-y-8">
            <FilterBar availableFilters={availableFilters} value={filters} onChange={setFilters} />
            <Heatmap heatmap={heatmap} metric={metric} onMetricChange={setMetric} onCellClick={openDrill} />
            {scores?.resources && (
              <ResourceSummary resources={scores.resources} meta={scores.resources_meta} register="team" />
            )}
            {scores && !scores.suppressed && (
              <FilteredCohortCard scores={scores} filters={filters} />
            )}
            {scores?.suppressed && Object.keys(filters).length > 0 && (
              <div className="panel" data-testid="filtered-suppressed">
                <div className="eyebrow mb-2" style={{ color: "#AD393E" }}>Filtered cohort suppressed</div>
                <p className="text-sm text-gray-700">
                  Only {scores.n} respondent{scores.n === 1 ? "" : "s"} match the current filter. Need 5+
                  to render scores.
                </p>
              </div>
            )}
          </div>
        )}

        {drillTarget && (
          <DrillDownPanel
            key={`${drillTarget.dimensionKey}::${drillTarget.segmentKey}::${drillTarget.segmentValue}`}
            deploymentId={id}
            dimensionKey={drillTarget.dimensionKey}
            segmentKey={drillTarget.segmentKey}
            segmentValue={drillTarget.segmentValue}
            triggerSelector={drillTriggerSelector}
            onClose={closeDrill}
          />
        )}

        {tab === "rubric" && <Rubric rubric={rubric} scores={scores} />}

        {tab === "divergence" && <Divergence deploymentId={id} filters={filters} />}

        {tab === "compare" && deployment && (
          <Compare
            deploymentId={id}
            demographicConfig={deployment.demographic_config}
            urlA={searchParams.get("compare_a")}
            urlB={searchParams.get("compare_b")}
            onSetUrl={(key, value, { also } = {}) => {
              const next = new URLSearchParams(searchParams);
              next.set(key, value);
              if (also) Object.entries(also).forEach(([k, v]) => next.set(k, v));
              setSearchParams(next);
            }}
          />
        )}

        {tab === "benchmark" && <Benchmark deploymentId={id} />}

        {tab === "cohorts" && (
          <Cohorts
            deploymentId={id}
            urlCohort={searchParams.get("cohort")}
            onSetCohort={(k) => {
              const next = new URLSearchParams(searchParams);
              next.set("cohort", k);
              setSearchParams(next);
            }}
          />
        )}

        {tab === "reflections" && <Reflections deploymentId={id} />}
      </main>
      <MarkedItemsTray />
      {editing && deployment && (
        <EditDeploymentModal
          deployment={deployment}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            setDeployment((d) => ({ ...d, ...updated }));
            setEditing(false);
          }}
        />
      )}
      </div>
    </MarkedItemsProvider>
  );
}
